/**
 * Story 074 — the canonical single-role resolver.
 *
 * The platform is SINGLE-ROLE, and that is enforced by the schema:
 * `auth_db.public.users.role_id` is ONE `NOT NULL` FK column — not an array,
 * not a join table. The `roles[]` array that arrives on the Zitadel JWT and on
 * `GET /api/auth/v1/me` is a **transport shape**, not a domain model.
 *
 * Every consumer must collapse it HERE so the answer is identical everywhere.
 * Before this module existed the collapse was implemented three times with
 * three different rules:
 *
 *   - `RoleContext.tsx`      → most-privileged via ROLE_PRIORITY
 *   - `ProfileSection.tsx`   → hand-rolled chain, tenant_admin checked FIRST
 *   - BE `me-self-service`   → `claims.roles[0]`, i.e. arbitrary array order
 *
 * A user holding both `platform_admin` and `tenant_admin` therefore resolved
 * differently depending on which code happened to run. The BE mirrors this
 * precedence in `@aeriosiq/identity` — keep the two in sync.
 *
 * This file is on the AC9 allowlist (`tests/static/no-direct-role-checks.spec.ts`):
 * it is a role-resolution primitive, so the comparison legitimately lives here.
 */
import type { Role } from "@aeriosiq/ui";

/**
 * Authority order. Higher wins when a JWT carries more than one role.
 * Mirrored on the BE — see `libs/identity/src/pick-primary-role.ts`.
 */
export const ROLE_PRIORITY: Readonly<Record<string, number>> = Object.freeze({
  platform_admin: 7,
  tenant_admin: 6,
  project_manager: 5,
  back_office: 4,
  expert_analyst: 3,
  pre_screener: 2,
  pilot: 1,
});

/**
 * Warn once per distinct role-set. A multi-role JWT is a DATA ANOMALY (the
 * platform-admin/tenant-admin overlap seen during the SB2-CC-02 Zitadel sync
 * rollout is the known case), so it should be visible rather than silently
 * resolved. Deduped because this runs inside render-path memos.
 */
const warnedRoleSets = new Set<string>();

/**
 * Collapse a transport `roles[]` to the single canonical role.
 * Returns `null` for an empty array — callers that need a display fallback
 * apply their own (e.g. ProfileSection falls back to `expert_analyst`, the
 * BE-058 default for a user with no explicit mapping).
 */
export function pickPrimaryRole(roles: readonly string[]): Role | null {
  if (roles.length === 0) return null;

  if (roles.length > 1) {
    const key = [...roles].sort().join(",");
    if (!warnedRoleSets.has(key)) {
      warnedRoleSets.add(key);
      console.warn(
        `[auth] multi-role JWT is a data anomaly — the platform is single-role. ` +
          `Collapsing [${key}] by authority order. Investigate the Zitadel grant.`,
      );
    }
  }

  let bestRole: string | null = null;
  let bestRank = -1;
  for (const r of roles) {
    const rank = ROLE_PRIORITY[r] ?? 0;
    if (rank > bestRank) {
      bestRank = rank;
      bestRole = r;
    }
  }
  return bestRole as Role | null;
}

/**
 * True iff the collapsed role equals `target`.
 *
 * Use this instead of `roles.includes("tenant_admin")`. The difference only
 * shows up for the anomalous multi-role case, and there the collapsed answer is
 * the correct one: a user is ONE role, so "is the caller a tenant_admin" must
 * mean "is tenant_admin the role they resolve to", not "is it somewhere in the
 * array".
 */
export function primaryRoleIs(roles: readonly string[], target: Role): boolean {
  return pickPrimaryRole(roles) === target;
}
